import { ref, type Ref } from "vue";

/**
 * SaveState is what the indicator shows after the last save the surface made:
 *
 *  - "idle"       nothing has been saved since the state was last cleared
 *  - "saving"     a save is in flight
 *  - "saved"      the last save reached the server; fades back to "idle"
 *  - "signed-out" the server refused the save because the session had ended —
 *                 the edit is NOT saved and is held for retry
 *  - "failed"     the save was rejected for its own sake — NOT saved either
 *
 * "signed-out" is separate from "failed" because it is the one failure the
 * operator can act on, and because collapsing the two is what made a lost
 * bulk edit invisible: a run of residual edits that 401'd showed the operator
 * nothing but "save failed", and the edits were gone.
 */
export type SaveState = "idle" | "saving" | "saved" | "signed-out" | "failed";

export interface SaveStateHandle {
	state: Ref<SaveState>;
	/** Edits accepted but not yet persisted, in the order they were made. */
	pending: Ref<number>;
	/** Queue one edit. Resolves once it is saved, rejected, or held. */
	run: (task: () => Promise<unknown>) => Promise<void>;
	/** Send the held edits again, e.g. after signing in. */
	retry: () => Promise<void>;
}

interface QueuedSave {
	task: () => Promise<unknown>;
	// Resolving the caller is all this queue does: the outcome of a save is
	// reported on the surface's indicator, not thrown at an event handler that
	// has nowhere to put it.
	settle: () => void;
}

function createSaveState(): SaveStateHandle {
	const state = ref<SaveState>("idle");
	const pending = ref(0);
	const queue: QueuedSave[] = [];
	let running: Promise<void> | null = null;
	let halted = false;
	// Each "saved" gets a token; only the newest token may fade, so a timer
	// left over from an earlier save cannot clear a later state.
	let savedTick = 0;

	// "saved" is a moment, not a state: it fades so the surface goes quiet
	// again. "signed-out" and "failed" never fade — a lost save stays legible
	// until the next edit, and while edits are held the indicator is the only
	// record that they exist.
	function scheduleIdle() {
		const tick = ++savedTick;
		setTimeout(() => {
			if (tick === savedTick && state.value === "saved") {
				state.value = "idle";
			}
		}, 2500);
	}

	// One edit at a time, in order. Firing the saves of a bulk edit
	// concurrently is what a change-driven auto-save does by default, and it
	// makes the outcome of the run depend on which request the server happened
	// to answer first — including which of several overlapping edits lands
	// last. Waiting for each save also means a save that fails is still the
	// save the indicator is describing.
	async function loop() {
		while (!halted && queue.length > 0) {
			const entry = queue[0];
			state.value = "saving";
			try {
				await entry.task();
			} catch (error) {
				// The server answers 401 with no body at all for the SPA's own
				// requests (X-Requested-With: nidus-web), so the status is the
				// only signal that it was the session, not the edit, that was
				// refused.
				const status = (error as { response?: { status?: number } })?.response
					?.status;
				if (status === 401) {
					// The session ended. Every edit behind this one would be
					// refused the same way, so nothing more is sent; the whole
					// queue — this edit included — is held for retry rather
					// than dropped, and the surface says so.
					halted = true;
					state.value = "signed-out";
					// This pass is over the moment the callers are released;
					// a retry from the continuation of one of them must start a
					// fresh pass rather than be told one is still running.
					running = null;
					for (const held of queue) {
						held.settle();
					}
					return;
				}
				// Refused for its own sake (a bad value, a missing row): it
				// would be refused again, so drop it — but never quietly. The
				// caller's edit stays on screen for the operator to correct.
				queue.shift();
				entry.settle();
				pending.value = queue.length;
				state.value = "failed";
				continue;
			}
			queue.shift();
			entry.settle();
			pending.value = queue.length;
			state.value = "saved";
			scheduleIdle();
		}
	}

	function resume(): Promise<void> {
		halted = false;
		if (running === null) {
			const started = loop();
			running = started;
			void started.finally(() => {
				// Only clear a pass that is still the current one: a pass that
				// halted has already cleared itself, and a retry may have
				// started a new one in the meantime.
				if (running === started) {
					running = null;
				}
			});
		}
		return running;
	}

	function run(task: () => Promise<unknown>): Promise<void> {
		return new Promise<void>((resolve) => {
			queue.push({ task, settle: resolve });
			pending.value = queue.length;
			// A new edit is also a chance to drain what is held: after signing
			// in again the very next field the operator touches flushes the
			// backlog instead of leaving it stranded behind the halt.
			void resume();
		});
	}

	return { state, pending, run, retry: resume };
}

// The queue lives beside the component, not inside it. The way out of a
// signed-out save is to sign in again, which routes away from the surface and
// back, and edits held for retry have to survive that round trip — otherwise
// the indicator's advice would destroy the very edits it is protecting.
const surfaces = new Map<string, SaveStateHandle>();

/**
 * The shared auto-save handle behind a no-button form. One handle per surface,
 * because the last edit's outcome is what the operator needs to see — a
 * spinner per keystroke would be noise. Pass the same `surface` from the same
 * page's components to share it, so held edits outlive the component.
 *
 * The fields that use this save on the native `change` event, which fires when
 * a text/number input commits (blur or Enter) rather than on every keystroke,
 * so no explicit debounce timer is needed; a select or checkbox commits at once.
 */
export function useSaveState(surface?: string): SaveStateHandle {
	if (surface === undefined) {
		return createSaveState();
	}
	let handle = surfaces.get(surface);
	if (handle === undefined) {
		handle = createSaveState();
		surfaces.set(surface, handle);
	}
	return handle;
}
